import React from 'react';
import { X, Calendar, Clock, User, Mail, MessageSquare, Globe, Phone, FileText, CreditCard, MapPin } from 'lucide-react';
import { Booking } from '../services/api';
import { t } from '../services/i18n';

interface BookingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
}

export const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({ isOpen, onClose, booking }) => {
  if (!isOpen || !booking) return null;

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="text-indigo-400" size={24} />
            Booking Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X size={24} className="text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Booking ID & Status */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400 mb-1">Booking Reference</div>
                <div className="font-mono text-lg font-bold text-indigo-400">#{booking.bookingId}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-400 mb-1">Status</div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  booking.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                  booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                  booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Service Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4">
              <div className="text-sm text-slate-400 mb-2">Service Type</div>
              <div className="text-white font-semibold text-lg">
                {booking.consultingType.toUpperCase()} Consulting
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4">
              <div className="text-sm text-slate-400 mb-2">Duration</div>
              <div className="text-white font-semibold text-lg flex items-center gap-2">
                <Clock size={18} className="text-indigo-400" />
                {booking.duration} minutes
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="text-indigo-400" size={20} />
              <div className="text-sm text-slate-400">Date & Time</div>
            </div>
            <div className="text-white font-semibold">{formatDate(booking.date)}</div>
            <div className="text-slate-300 mt-1">
              {booking.startTime} - {booking.endTime} (Scheduled Time)
            </div>
            {booking.googleMeet?.meetingLink && (
              <a
                href={booking.googleMeet.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                <Globe size={16} />
                Join Google Meet
              </a>
            )}
          </div>

          {/* Client Information */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="text-indigo-400" size={20} />
              <div className="text-lg font-semibold text-white">Client Information</div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-sm text-slate-400 mb-1">Full Name</div>
                <div className="text-white font-medium">{booking.clientInfo.fullName}</div>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-slate-400" />
                <div>
                  <div className="text-sm text-slate-400 mb-1">Email</div>
                  <div className="text-white">{booking.clientInfo.email}</div>
                </div>
              </div>
              {booking.clientInfo.telegram && (
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-slate-400" />
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Telegram</div>
                    <div className="text-white">{booking.clientInfo.telegram}</div>
                  </div>
                </div>
              )}
              {booking.clientInfo.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-slate-400" />
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Phone</div>
                    <div className="text-white">{booking.clientInfo.phone}</div>
                  </div>
                </div>
              )}
              {booking.clientInfo.website && (
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-slate-400" />
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Website</div>
                    <a
                      href={booking.clientInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-400 hover:text-indigo-300"
                    >
                      {booking.clientInfo.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Consultation Topic */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="text-sm text-slate-400 mb-2">Consultation Topic</div>
            <div className="text-white whitespace-pre-wrap">{booking.consultationDetails.topic}</div>
          </div>

          {/* Payment Information */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="text-indigo-400" size={20} />
              <div className="text-lg font-semibold text-white">Payment Information</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-slate-400 mb-1">Amount</div>
                <div className="text-2xl font-bold text-indigo-400">{booking.price} {booking.payment.currency}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400 mb-1">Payment Status</div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold inline-block ${
                  booking.payment.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  booking.payment.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  booking.payment.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {booking.payment.status.charAt(0).toUpperCase() + booking.payment.status.slice(1)}
                </span>
              </div>
            </div>
            {booking.payment.paypalTransactionId && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="text-sm text-slate-400 mb-1">PayPal Transaction ID</div>
                <div className="font-mono text-xs text-slate-300">{booking.payment.paypalTransactionId}</div>
              </div>
            )}
            {booking.payment.paidAt && (
              <div className="mt-2">
                <div className="text-sm text-slate-400 mb-1">Paid At</div>
                <div className="text-slate-300">{formatDate(booking.payment.paidAt)}</div>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-400 mb-1">Created</div>
              <div className="text-slate-300">{formatDate(booking.createdAt)}</div>
            </div>
            <div>
              <div className="text-slate-400 mb-1">Last Updated</div>
              <div className="text-slate-300">{formatDate(booking.updatedAt)}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

